import { useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type {
    SectionKey,
    SectionVisibilityRequest,
    SectionVisibilityResponse,
    UserProfileVisibilityRequest,
} from '~/shared/data-contract/profile-visibility';
import type { User } from '~/shared/data-contract/user';
import { useUserProfileVisibilityMutation } from '~/shared/query-hooks/profile-visibility';

import { useSnackbars } from '../../../error-handling/SnackBars';

const emptySectionData = {
    everyone: false,
    roles: [],
    users: [],
};

type ProfileVisibilityFormControllerProps = {
    readonly getSectionValue: (sectionKey: SectionKey) => SectionVisibilityResponse;
    readonly refetch: () => void;
    readonly setSectionValue: (sectionKey: SectionKey, value: SectionVisibilityResponse) => void;
};

type ProfileVisibilityFormControllerResponse = {
    readonly mutationInProgress: boolean;
    readonly onEveryoneClick: (sectionKey: SectionKey) => () => void;
    readonly onResetConfiguration: () => void;
    readonly onResetSection: (sectionKey: SectionKey) => (applyToOtherSections: boolean) => void;
    readonly onRoleClick: (sectionKey: SectionKey) => (role: string) => void;
    readonly onSaveSection: (sectionKey: SectionKey) => (applyToOtherSections: boolean) => void;
    readonly onUserClick: (sectionKey: SectionKey) => (user: User) => void;
};

export const useProfileVisibilityFormController = ({
    getSectionValue,
    refetch,
    setSectionValue,
}: ProfileVisibilityFormControllerProps): ProfileVisibilityFormControllerResponse => {
    const { applicationState } = useContext(ApplicationContext);
    const { myUserProfile } = applicationState;
    const userId = myUserProfile.get('id').unwrapOr('');
    const profileMutation = useUserProfileVisibilityMutation(userId);
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    const mutate = (data: UserProfileVisibilityRequest): void =>
        profileMutation.mutate(data, {
            onError: showErrorSnackbar,
            onSettled: () => {
                refetch();
            },
            onSuccess: () => showSuccessSnackbar('success.profile-visibility.updated'),
        });

    const onRoleClick =
        (sectionKey: SectionKey) =>
        (role: string): void => {
            const sectionValue = getSectionValue(sectionKey);

            const roleAlreadyWasSelected = sectionValue.roles.includes(role);

            setSectionValue(sectionKey, {
                ...sectionValue,
                everyone: false,
                roles: roleAlreadyWasSelected
                    ? sectionValue.roles.filter((r) => r !== role)
                    : [...sectionValue.roles, role],
            });
        };

    const onEveryoneClick = (sectionKey: SectionKey) => (): void => {
        const sectionValue = getSectionValue(sectionKey);

        setSectionValue(sectionKey, {
            everyone: !sectionValue.everyone,
            roles: [],
            users: [],
        });
    };

    const onUserClick =
        (sectionKey: SectionKey) =>
        (user: User): void => {
            const sectionValue = getSectionValue(sectionKey);

            const userAlreadyWasSelected = sectionValue.users.map(({ id }) => id).includes(user.id);

            setSectionValue(sectionKey, {
                ...sectionValue,
                everyone: false,
                users: userAlreadyWasSelected
                    ? sectionValue.users.filter(({ id }) => id !== user.id)
                    : [...sectionValue.users, user],
            });
        };

    const saveSection = (
        sectionKey: SectionKey,
        sectionValues: SectionVisibilityResponse,
        applyToOtherSections: boolean,
    ): void => {
        const sectionDataForUpdate = (sectionKey: SectionKey): SectionVisibilityRequest => {
            const section = getSectionValue(sectionKey);

            return {
                ...section,
                users: section.users.map(({ id }) => id),
            };
        };

        if (applyToOtherSections) {
            setSectionValue('skillsVisibility', sectionValues);
            setSectionValue('ambitionsVisibility', sectionValues);
            setSectionValue('historyVisibility', sectionValues);
            setSectionValue('personalInfoVisibility', sectionValues);

            const sectionsRequestData: UserProfileVisibilityRequest = {
                ambitionsVisibility: sectionDataForUpdate('ambitionsVisibility'),
                historyVisibility: sectionDataForUpdate('historyVisibility'),
                personalInfoVisibility: sectionDataForUpdate('personalInfoVisibility'),
                skillsVisibility: sectionDataForUpdate('skillsVisibility'),
            };

            mutate(sectionsRequestData);
        } else {
            setSectionValue(sectionKey, sectionValues);

            mutate({
                [sectionKey]: sectionDataForUpdate(sectionKey),
            });
        }
    };

    const onSaveSection =
        (sectionKey: SectionKey) =>
        (applyToOtherSections: boolean): void => {
            const sectionValues = getSectionValue(sectionKey);
            saveSection(sectionKey, sectionValues, applyToOtherSections);
        };

    const onResetSection =
        (sectionKey: SectionKey) =>
        (applyToOtherSections: boolean): void => {
            saveSection(sectionKey, emptySectionData, applyToOtherSections);
        };

    const onResetConfiguration = (): void => {
        saveSection('skillsVisibility', emptySectionData, true);
    };

    return {
        mutationInProgress: profileMutation.isPending,
        onEveryoneClick,
        onResetConfiguration,
        onResetSection,
        onRoleClick,
        onSaveSection,
        onUserClick,
    };
};
